import { setFocus } from '@noriginmedia/norigin-spatial-navigation';
import useFavoritedContent from '@smart-tv/hooks/use-favorited-content';
import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { usePlayerContext } from '../../player/_contexts/player-context';
import { useNavigate, useParams } from 'react-router-dom';
import { Universe } from '@smart-tv/typings/content';
import useEventListener from '@smart-tv/hooks/use-event-listener';
import {
  UrlParams,
  instantScrollBehaviorOnFocusKey,
  PageType,
  PROVIDER_MENU_HEIGHT,
  HERO_ELEMENT_HEIGHT,
  TOP_BAR_HEIGHT,
  STRIPE_ITEM_HEIGHT
} from '../_utils/types';
import { animatedScroll } from '../_utils/utils';
import { useAtomValue, useSetAtom } from 'jotai';
import useStripesProviderStore from '../_stores/useStripesProviderStore';
import { pageAreaAtom, isHeroRenderedAtom } from '@smart-tv/atoms';

import { rootStore } from '../_stores/store-root';
import useStripesBase from '../_hooks/use-stripes-base';
import { testIds } from '@smart-tv/e2e/shared/variables';
import { getAreaForPageType } from '@smart-tv/utils/kmm/helpers';
import { isBackKey } from '@smart-tv/utils/remoteKeys';

enum NetworkErrorSource {
  Universe = 'universe',
  ShowAll = 'showAll'
}

const useStripesProvider = (props: { pageType: PageType }) => {
  const { pageType } = props;
  const { universeTitle, providerId } = useParams() as UrlParams;
  const navigate = useNavigate();
  const { player } = usePlayerContext();
  const { favoritedContent } = useFavoritedContent();
  const isHeroRendered = useAtomValue(isHeroRenderedAtom);
  const [isProviderMenuRendered, setIsProviderMenuRendered] = useState<boolean>(false);
  const setPageAreaAtom = useSetAtom(pageAreaAtom);

  const lastAttemptedUniverseId = useRef<number | null>(null);

  const firstload = useRef<boolean>(true);

  const stripesStore = useStripesProviderStore({
    providerId: parseInt(providerId)
  });

  const lastNetworkErrorSource = useRef<NetworkErrorSource | null>(null);

  const {
    ref,
    focusKey,
    openDialog,
    openCollections,
    handleOpen,
    handleonShowAllEnterPress,
    savedFocusKeyBeforeError,
    savedUIElementBeforeError,
    checkAndUpdateNetwork,
    setIsOffline
  } = useStripesBase({
    pageType,
    stripesStore,
    onBeforeShowAll: () => {
      lastNetworkErrorSource.current = NetworkErrorSource.ShowAll;
    }
  });

  useEffect(() => {
    setPageAreaAtom(getAreaForPageType(pageType, undefined, parseInt(providerId)));
  }, [providerId, setPageAreaAtom, pageType]);

  useEffect(() => {
    if (favoritedContent) {
      stripesStore.fetchGenres();
    }
  }, [favoritedContent, stripesStore]);

  const onHeroUIElementFocus = useCallback(() => {
    // Use instant scrolling when provider universe is initailly rendered to avoid jarring scroll animation on page load
    if (firstload.current) {
      sessionStorage.setItem(instantScrollBehaviorOnFocusKey, 'true');
      firstload.current = false;
    }
    animatedScroll(0);
  }, []);

  const onProviderMenuUIElementFocus = useCallback(() => {
    const offset = TOP_BAR_HEIGHT + HERO_ELEMENT_HEIGHT;
    animatedScroll(offset);
  }, []);

  const onProviderUIElementFocus = useCallback(({ y }: { y: number }) => {
    const offset = TOP_BAR_HEIGHT + HERO_ELEMENT_HEIGHT + STRIPE_ITEM_HEIGHT - PROVIDER_MENU_HEIGHT;
    animatedScroll(y + offset);
  }, []);

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (!player && !openDialog && !openCollections && isBackKey(event)) {
        navigate('/');
      }
    },
    [navigate, openCollections, openDialog, player]
  );

  const handleSelectUniverse = useCallback(
    async (universeId: number) => {
      lastNetworkErrorSource.current = NetworkErrorSource.Universe;
      lastAttemptedUniverseId.current = universeId;
      setIsOffline(false);
      const hasNetwork = await checkAndUpdateNetwork();

      if (!hasNetwork) return;
      rootStore.uiStoreProvider.handleSelectUniverse(universeId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rootStore is a module-level singleton and never changes
    [checkAndUpdateNetwork, setIsOffline]
  );

  const navigationContent = useMemo(
    () =>
      stripesStore.responseNavigationContent?.map((universe: Universe) => ({
        label: universe.title,
        selected: universe.id === stripesStore.selectedUniverseId,
        dataTestId: testIds.providerUniverse.menuBarItem,
        onEnterPress: () => handleSelectUniverse(universe.id)
      })),
    [stripesStore.responseNavigationContent, stripesStore.selectedUniverseId, handleSelectUniverse]
  );

  useEffect(() => {
    if (stripesStore.responseNavigationContent.length > 0 && !stripesStore.selectedUniverseId) {
      // Select the first universe if no universe is selected as default
      const firstUniverseId = stripesStore.responseNavigationContent[0].id;
      handleSelectUniverse(firstUniverseId);
    }
  }, [
    stripesStore.responseNavigationContent,
    stripesStore.selectedUniverseId,
    handleSelectUniverse
  ]);

  const retryNetworkAction = useCallback(async () => {
    if (lastNetworkErrorSource.current === NetworkErrorSource.ShowAll) {
      // Restore the focus key that was saved before the error dialog appeared
      if (savedFocusKeyBeforeError.current) {
        setFocus(savedFocusKeyBeforeError.current);
      }
      if (savedUIElementBeforeError.current) {
        stripesStore.setActiveUIElement(savedUIElementBeforeError.current);
      }
      // handleOpen will capture the focus key synchronously after setFocus completes
      handleOpen();
    }
    if (lastNetworkErrorSource.current === NetworkErrorSource.Universe) {
      if (lastAttemptedUniverseId.current) {
        handleSelectUniverse(lastAttemptedUniverseId.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ref objects (savedFocusKeyBeforeError, savedUIElementBeforeError, lastNetworkErrorSource, lastAttemptedUniverseId) are stable and intentionally omitted
  }, [stripesStore, handleOpen, handleSelectUniverse]);

  useEventListener('keydown', (event: KeyboardEvent) => handleKeyPress(event));

  return {
    ref,
    focusKey,
    uiElements: stripesStore.uiElements,
    heroUiElements: stripesStore.heroUiElements,
    activeUIElement: stripesStore.activeUIElement,
    loading: stripesStore.loading,
    openCollections,
    handleonShowAllEnterPress,
    onHeroUIElementFocus,
    onProviderMenuUIElementFocus,
    universeTitle,
    isHeroRendered,
    isProviderMenuRendered,
    setIsProviderMenuRendered,
    retryNetworkAction,
    navigationContent,
    lastNetworkErrorSource,
    onProviderUIElementFocus
  };
};

export default useStripesProvider;
